/* eslint-disable no-console */
import { getAuth0ClientId } from './auth0Config';

/**
 * Utility functions for BDD token injection
 * These functions help inject tokens directly into Auth0 SDK's storage
 * to bypass the normal authentication flow for testing
 */

export interface BDDTokenResponse {
  userKey: string;
  accessTokenKey: string;
  userLanguage: string;
  redirectUrl: string;
}

// TypeScript interfaces for Auth0 SDK internal storage structure
interface Auth0UserObject {
  id_token: string;
  decodedToken: {
    encoded: {
      header: string;
      payload: string;
      signature: string;
    };
    header: {
      alg: string;
      typ: string;
      kid: string;
    };
    claims: {
      __raw: string;
      groupmember: boolean;
      company: string;
      migrated: string;
      allowltda: string;
      oldflow: string;
      groups_member: string[];
      roles_user: string[];
      given_name: string;
      family_name: string;
      nickname: string;
      preferred_username: string;
      name: string;
      picture: string;
      updated_at: string;
      email: string;
      iss: string;
      aud: string;
      sub: string;
      iat: number;
      exp: number;
      sid: string;
      nonce: string;
      [key: string]: any;
    };
    user: {
      groupmember: boolean;
      company: string;
      migrated: string;
      allowltda: string;
      oldflow: string;
      groups_member: string[];
      roles_user: string[];
      given_name: string;
      family_name: string;
      nickname: string;
      preferred_username: string;
      name: string;
      picture: string;
      updated_at: string;
      email: string;
      sub: string;
      [key: string]: any;
    };
  };
}

interface Auth0AccessTokenObject {
  body: {
    access_token: string;
    refresh_token: string;
    scope: string;
    expires_in: number;
    token_type: string;
    audience: string;
    oauthTokenScope: string;
    client_id: string;
  };
  expiresAt: number;
}

/**
 * Injects tokens directly into Auth0 SDK's internal storage
 * This bypasses the normal Auth0 flow and makes the SDK think the user is authenticated
 */
export const injectBDDTokensAndReturnUserDetails = (
  response: BDDTokenResponse,
) => {
  try {
    const auth0Storage = window.localStorage;
    const clientId = getAuth0ClientId();

    // Auth0 SDK uses these specific key formats
    const userKey = `@@auth0spajs@@::${clientId}::@@user@@`;
    const accessTokenKey = `@@auth0spajs@@::${clientId}::ltda-api::openid email offline_access profile`;

    // Parse the JSON strings into objects
    const userKeyObject: Auth0UserObject = JSON.parse(response.userKey);
    const accessTokenKeyObject: Auth0AccessTokenObject = JSON.parse(
      response.accessTokenKey,
    );

    // Merge `claims` into `user` to add missing properties and ensure a complete user object for BDDs
    const { user, claims } = userKeyObject.decodedToken;
    userKeyObject.decodedToken.user = { ...user, ...claims };

    // Store the parsed objects as JSON strings
    auth0Storage.setItem(userKey, JSON.stringify(userKeyObject));
    auth0Storage.setItem(accessTokenKey, JSON.stringify(accessTokenKeyObject));

    console.log('BDD tokens injected successfully into Auth0 SDK');
  } catch (error) {
    console.error('Error injecting BDD tokens:', error);
    throw error;
  }
};
